import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { paperRequest } from './utils/paperClient';
import "./MobilePaperUpload.css";
import studydenHorizontal from "./assets/images/branding/Studyden_Horizontal.svg";

/**
 * MobilePaperUpload
 *
 * Accessed via /mobile-paper-upload/:sessionId — typically by scanning a QR code.
 * No authentication required.
 *
 * Photos use the scoped backend capability; clients never receive teacher credentials.
 */
const MobilePaperUpload = () => {
  const { sessionId } = useParams();
  const token = useRef((() => { try { return decodeURIComponent(window.location.hash.slice(1)); } catch { return ""; } })()).current;
  const uploadIds = useRef(new WeakMap());
  const previewUrlsRef = useRef([]);

  // ===== SESSION STATE =====
  const [status, setStatus] = useState("loading"); // "loading" | "active" | "expired" | "invalid"
  const [sessionData, setSessionData] = useState(null);
  const [expiresAtDate, setExpiresAtDate] = useState(null);

  // ===== UPLOAD STATE =====
  const [selectedFiles, setSelectedFiles] = useState([]);  // File objects chosen by user
  const [previews, setPreviews] = useState([]);             // Object URLs for thumbnails
  previewUrlsRef.current = previews;
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0); // 0–100
  const [uploadComplete, setUploadComplete] = useState(false);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [uploadError, setUploadError] = useState(null);

  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  // ===== FETCH SESSION =====
  useEffect(() => {
    if (!sessionId) {
      setStatus("invalid");
      return;
    }

    const fetchSession = async () => {
      try {
        if (!token) { setStatus('invalid'); return; }
        const data = await paperRequest('/api/paper-upload-sessions/' + sessionId + '/info', {}, token);
        const expiresAt = new Date(data.expiresAt);
        setSessionData(data);
        setExpiresAtDate(expiresAt);
        setStatus("active");
      } catch (err) {
        void 0;
        setStatus(err.status === 410 ? "expired" : "invalid");
      }
    };

    fetchSession();
  }, [sessionId, token]);

  // ===== CLEANUP PREVIEW URLS =====
  useEffect(() => {
    return () => {
      previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  // ===== FILE SELECTION =====
  const handleFilesSelected = (e) => {
    const newFiles = Array.from(e.target.files || []);
    if (newFiles.length === 0) return;

    // Validate — images only
    const imageFiles = newFiles.filter((f) => ["image/jpeg", "image/png"].includes(f.type));
    if (imageFiles.length !== newFiles.length) {
      alert("Only JPEG and PNG image files are supported.");
    }
    if (imageFiles.length === 0) return;

    // Validate — max 25 pages total
    const combined = [...selectedFiles, ...imageFiles];
    if (combined.length > 25) {
      alert(`Maximum 25 images per session. You can add ${25 - selectedFiles.length} more.`);
      const allowed = imageFiles.slice(0, 25 - selectedFiles.length);
      if (allowed.length === 0) return;
      const newPreviews = allowed.map((f) => URL.createObjectURL(f));
      setSelectedFiles((prev) => [...prev, ...allowed]);
      setPreviews((prev) => [...prev, ...newPreviews]);
      return;
    }

    const newPreviews = imageFiles.map((f) => URL.createObjectURL(f));
    setSelectedFiles((prev) => [...prev, ...imageFiles]);
    setPreviews((prev) => [...prev, ...newPreviews]);

    // Reset the input so the same file can be re-selected if needed
    e.target.value = "";
  };

  const handleRemoveFile = (index) => {
    URL.revokeObjectURL(previews[index]);
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    setPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  // ===== REORDER =====
  const moveFile = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= selectedFiles.length) return;
    const moveInArray = (arr) => {
      const result = [...arr];
      const [item] = result.splice(fromIndex, 1);
      result.splice(toIndex, 0, item);
      return result;
    };
    setSelectedFiles((prev) => moveInArray(prev));
    setPreviews((prev) => moveInArray(prev));
  };

  // ===== UPLOAD =====
  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    setUploading(true);
    setUploadError(null);
    setUploadProgress(0);

    try {
      const downloadUrls = [];
      const total = selectedFiles.length;

      for (let i = 0; i < total; i++) {
        const file = selectedFiles[i];
        if (!uploadIds.current.has(file)) uploadIds.current.set(file, crypto.randomUUID());
        const imageBase64 = await new Promise((resolve, reject) => {
          const reader = new FileReader(); reader.onerror = () => reject(new Error('The image could not be read.'));
          reader.onload = () => resolve(String(reader.result).split(',')[1]); reader.readAsDataURL(file);
        });
        const result = await paperRequest('/api/paper-upload-sessions/' + sessionId + '/images', { uploadId: uploadIds.current.get(file), imageBase64 }, token);
        downloadUrls.push(result.objectPath);

        // Update progress
        setUploadProgress(Math.round(((i + 1) / total) * 100));
      }

      setUploadedCount(downloadUrls.length);
      setUploadComplete(true);
      setSelectedFiles([]);
      setPreviews([]);
    } catch (err) {
      setUploadError(
        err.message || "Upload failed. Please check your connection and try again."
      );
    } finally {
      setUploading(false);
    }
  };

  // ===== HELPERS =====
  const formatExpiry = (date) => {
    if (!date) return "";
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  // ===== RENDER =====
  const renderContent = () => {
    switch (status) {
      case "loading":
        return (
          <div className="mobile-upload-loading">
            <div className="mobile-upload-spinner" aria-label="Loading" />
            <p>Loading your upload session…</p>
          </div>
        );

      case "active":
        return (
          <div className="mobile-upload-active">
            <span className="mobile-upload-icon">📄</span>
            <p className="session-label">Paper Upload Session</p>
            <h1>Upload pages for {sessionData.studentName}</h1>

            <div className="session-meta">
              <div className="session-meta-row">
                <span className="session-meta-label">Quiz</span>
                <span className="session-meta-value">{sessionData.quizTitle}</span>
              </div>
              <div className="session-meta-row">
                <span className="session-meta-label">Student</span>
                <span className="session-meta-value">{sessionData.studentName}</span>
              </div>
            </div>

            {/* Success banner — shown after a successful upload */}
            {uploadComplete && (
              <div className="mobile-upload-success">
                <span className="success-icon">✅</span>
                <p className="success-msg">
                  {uploadedCount} image{uploadedCount !== 1 ? "s" : ""} uploaded successfully!
                </p>
                <p className="success-sub">
                  Your teacher can now see them. You can add more images below if needed.
                </p>
                <button
                  className="mobile-btn mobile-btn-secondary"
                  onClick={() => setUploadComplete(false)}
                >
                  Upload more
                </button>
              </div>
            )}

            {/* Upload controls — shown when not in success state */}
            {!uploadComplete && (
              <>
                {/* File selection buttons */}
                <div className="mobile-upload-controls">
                  <p className="upload-instructions">
                    Take a photo of each page or choose from your gallery.
                  </p>

                  <div className="mobile-btn-row">
                    {/* Camera capture */}
                    <button
                      className="mobile-btn mobile-btn-primary"
                      onClick={() => cameraInputRef.current?.click()}
                      disabled={uploading || selectedFiles.length >= 25}
                    >
                      📷 Take photo
                    </button>

                    {/* Gallery pick */}
                    <button
                      className="mobile-btn mobile-btn-secondary"
                      onClick={() => galleryInputRef.current?.click()}
                      disabled={uploading || selectedFiles.length >= 25}
                    >
                      🖼️ Choose from gallery
                    </button>
                  </div>

                  {/* Hidden inputs */}
                  <input
                    ref={cameraInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    style={{ display: "none" }}
                    onChange={handleFilesSelected}
                  />
                  <input
                    ref={galleryInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    style={{ display: "none" }}
                    onChange={handleFilesSelected}
                  />
                </div>

                {/* Thumbnails + reorder list */}
                {previews.length > 0 && (
                  <div className="mobile-thumbnails-section">
                    <div className="thumbnails-header">
                      <p className="thumbnails-label">
                        {selectedFiles.length} page{selectedFiles.length !== 1 ? "s" : ""} selected
                        {selectedFiles.length >= 25 && (
                          <span className="max-badge"> (max reached)</span>
                        )}
                      </p>
                      {selectedFiles.length > 1 && !uploading && (
                        <p className="reorder-hint">Use ↑ ↓ to reorder</p>
                      )}
                    </div>

                    <div className="mobile-page-list">
                      {previews.map((src, idx) => (
                        <div key={src} className="mobile-page-row">
                          {/* Thumbnail */}
                          <div className="mobile-page-thumb">
                            <img src={src} alt={`Page ${idx + 1}`} />
                          </div>

                          {/* Info */}
                          <div className="mobile-page-info">
                            <span className="mobile-page-badge">Page {idx + 1}</span>
                            <span className="mobile-page-name" title={selectedFiles[idx]?.name}>
                              {selectedFiles[idx]?.name}
                            </span>
                          </div>

                          {/* Controls */}
                          <div className="mobile-page-controls">
                            <button
                              className="mobile-reorder-btn"
                              onClick={() => moveFile(idx, idx - 1)}
                              disabled={uploading || idx === 0}
                              aria-label={`Move page ${idx + 1} up`}
                            >
                              ↑
                            </button>
                            <button
                              className="mobile-reorder-btn"
                              onClick={() => moveFile(idx, idx + 1)}
                              disabled={uploading || idx === selectedFiles.length - 1}
                              aria-label={`Move page ${idx + 1} down`}
                            >
                              ↓
                            </button>
                            <button
                              className="mobile-reorder-btn mobile-reorder-btn--remove"
                              onClick={() => handleRemoveFile(idx)}
                              disabled={uploading}
                              aria-label={`Remove page ${idx + 1}`}
                            >
                              ✕
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Upload button */}
                    <button
                      className="mobile-btn mobile-btn-upload"
                      onClick={handleUpload}
                      disabled={uploading}
                    >
                      {uploading
                        ? `Uploading… ${uploadProgress}%`
                        : `Upload ${selectedFiles.length} image${selectedFiles.length !== 1 ? "s" : ""}`}
                    </button>

                    {/* Progress bar */}
                    {uploading && (
                      <div className="mobile-progress-bar">
                        <div
                          className="mobile-progress-fill"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                    )}

                    {/* Error */}
                    {uploadError && (
                      <div className="mobile-upload-error-msg">
                        <p>⚠️ {uploadError}</p>
                        <button
                          className="mobile-btn mobile-btn-secondary"
                          onClick={handleUpload}
                        >
                          Retry
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {expiresAtDate && (
              <p className="session-expiry">
                Session expires at {formatExpiry(expiresAtDate)}
              </p>
            )}
          </div>
        );

      case "expired":
        return (
          <div className="mobile-upload-expired">
            <span className="mobile-upload-icon">⏰</span>
            <h2>This upload session has expired</h2>
            <p>Please ask your teacher to generate a new QR code for you.</p>
          </div>
        );

      case "invalid":
      default:
        return (
          <div className="mobile-upload-error">
            <span className="mobile-upload-icon">❌</span>
            <h2>Invalid upload session</h2>
            <p>This link is not recognised. Please scan the QR code again or ask your teacher for help.</p>
          </div>
        );
    }
  };

  return (
    <div className="mobile-upload-page">
      <div className="mobile-upload-card">
        {renderContent()}
      </div>
      <p className="mobile-upload-footer"><img src={studydenHorizontal} alt="StudyDen" /> <span>Secure Upload</span></p>
    </div>
  );
};

export default MobilePaperUpload;
